<template>
  <div class="manual-add-page">
    <h1 class="page-title">Ручное добавление поста</h1>
    <p class="page-subtitle">
      Добавьте пост вручную. Используйте AI для генерации описания и определения ФО.
    </p>

    <el-card class="form-card" shadow="hover">
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="top"
        @submit.prevent="handleSubmit"
      >
        <!-- Content -->
        <el-form-item label="Текст поста" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="6"
            placeholder="Вставьте текст поста..."
            maxlength="8000"
            show-word-limit
          />
        </el-form-item>

        <!-- AI Analyze Button -->
        <el-form-item>
          <el-button
            type="primary"
            :loading="isAnalyzing"
            :disabled="!form.content || isAnalyzing"
            @click="handleAnalyze"
          >
            <el-icon class="ai-icon"><MagicStick /></el-icon>
            Проанализировать текст
          </el-button>
        </el-form-item>

        <!-- AI Explanation (editable) -->
        <el-form-item v-if="analysisData.summary" label="AI-описание (можно редактировать)">
          <el-input
            v-model="form.explanation"
            type="textarea"
            :rows="3"
            placeholder="Описание будет сгенерировано AI..."
            maxlength="5000"
            show-word-limit
          />
          <div class="analysis-meta">
            <el-tag v-if="analysisData.category" size="small" type="info">
              Категория: {{ analysisData.category }}
            </el-tag>
            <el-tag v-if="analysisData.confidence !== undefined" size="small" :type="confidenceTagType">
              Уверенность: {{ Math.round(analysisData.confidence * 100) }}%
            </el-tag>
            <el-tag v-if="analysisData.is_complaint !== undefined" size="small" :type="analysisData.is_complaint ? 'danger' : 'success'">
              {{ analysisData.is_complaint ? 'Жалоба' : 'Не жалоба' }}
            </el-tag>
          </div>
        </el-form-item>

        <!-- Published At -->
        <el-form-item label="Дата и время публикации" prop="published_at">
          <el-date-picker
            v-model="form.published_at"
            type="datetime"
            placeholder="Выберите дату и время"
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DDTHH:mm:ss"
            :disabled-date="disabledFutureDate"
          />
        </el-form-item>

        <!-- Views -->
        <el-form-item label="Количество просмотров" prop="views">
          <el-input-number
            v-model="form.views"
            :min="0"
            placeholder="0"
          />
        </el-form-item>

        <!-- Federal District -->
        <el-form-item label="ФО" prop="federal_district">
          <el-select
            v-model="form.federal_district"
            placeholder="Выберите округ или определите AI"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="code in districtOptions"
              :key="code"
              :label="`${code} — ${districtNames[code]}`"
              :value="code"
            />
          </el-select>
        </el-form-item>

        <!-- URL -->
        <el-form-item label="Ссылка на оригинальный пост" prop="url">
          <el-input
            v-model="form.url"
            placeholder="https://..."
            clearable
          />
        </el-form-item>

        <!-- Actions -->
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="handleSubmit"
          >
            {{ isSubmitting ? 'Сохранение...' : 'Сохранить пост' }}
          </el-button>
          <el-button
            size="large"
            :disabled="isSubmitting"
            @click="handleClear"
          >
            Очистить
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Duplicate Warning Dialog -->
    <el-dialog
      v-model="duplicateDialogVisible"
      title="Дубликат обнаружен"
      width="400px"
    >
      <p>Пост с таким содержимым уже существует.</p>
      <p><strong>ID поста:</strong> {{ duplicatePostId }}</p>
      <template #footer>
        <el-button @click="duplicateDialogVisible = false">Закрыть</el-button>
        <el-button type="primary" :disabled="duplicatePostId === null" @click="duplicatePostId !== null && goToPost(duplicatePostId)">
          Перейти к посту
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElForm } from 'element-plus'
import { MagicStick } from '@element-plus/icons-vue'
import {
  createManualPost,
  summarizeText,
} from '@/api/client'
import { extractDuplicatePostId } from '@/views/manual-duplicates'
import type { FormInstance, FormRules } from 'element-plus'

const router = useRouter()
const formRef = ref<FormInstance>()

// ═════════════════════════════════════════════════════════════════════════════
// Constants
// ═════════════════════════════════════════════════════════════════════════════

const DRAFT_KEY = 'manual-post-draft'

const districtOptions = [
  'ЦФО', 'СЗФО', 'ЮФО', 'СКФО', 'ПФО', 'УрФО', 'СФО', 'ДФО', 'НЕОПР',
]

const districtNames: Record<string, string> = {
  'ЦФО': 'Центральный ФО',
  'СЗФО': 'Северо-Западный ФО',
  'ЮФО': 'Южный ФО',
  'СКФО': 'Северо-Кавказский ФО',
  'ПФО': 'Приволжский ФО',
  'УрФО': 'Уральский ФО',
  'СФО': 'Сибирский ФО',
  'ДФО': 'Дальневосточный ФО',
  'НЕОПР': 'Неопределённый',
}

// ═════════════════════════════════════════════════════════════════════════════
// Form state
// ═════════════════════════════════════════════════════════════════════════════

interface FormState {
  content: string
  published_at: string
  views: number | undefined
  federal_district: string
  url: string
  explanation: string
}

const form = reactive<FormState>({
  content: '',
  published_at: '',
  views: undefined,
  federal_district: '',
  url: '',
  explanation: '',
})

const rules: FormRules<FormState> = {
  content: [
    { required: true, message: 'Введите текст поста', trigger: 'blur' },
    { min: 1, max: 8000, message: 'Длина от 1 до 8000 символов', trigger: 'blur' },
  ],
  published_at: [
    {
      validator: (_rule: unknown, value: string, callback: (error?: Error) => void) => {
        if (value) {
          const date = new Date(value)
          if (date > new Date()) {
            callback(new Error('Дата не может быть в будущем'))
            return
          }
        }
        callback()
      },
      trigger: 'change',
    },
  ],
  views: [
    {
      validator: (_rule: unknown, value: number | undefined, callback: (error?: Error) => void) => {
        if (value !== undefined && value !== null && value < 0) {
          callback(new Error('Количество просмотров не может быть отрицательным'))
          return
        }
        callback()
      },
      trigger: 'change',
    },
  ],
}

// ═════════════════════════════════════════════════════════════════════════════
// UI state
// ═════════════════════════════════════════════════════════════════════════════

const isSubmitting = ref(false)
const isAnalyzing = ref(false)
const duplicateDialogVisible = ref(false)
const duplicatePostId = ref<number | null>(null)

// Analysis data from AI (for meta display)
interface AnalysisData {
  summary: string
  federal_district: string
  region_name: string | null
  confidence: number
  category: string
  is_complaint: boolean
}

const analysisData = reactive<AnalysisData>({
  summary: '',
  federal_district: '',
  region_name: null,
  confidence: 0,
  category: '',
  is_complaint: false,
})

const confidenceTagType = computed(() => {
  const c = analysisData.confidence
  if (c >= 0.8) return 'success'
  if (c >= 0.5) return 'warning'
  return 'danger'
})

// ═════════════════════════════════════════════════════════════════════════════
// Auto-save draft
// ═════════════════════════════════════════════════════════════════════════════

function saveDraft() {
  localStorage.setItem(DRAFT_KEY, JSON.stringify({
    content: form.content,
    published_at: form.published_at,
    views: form.views,
    federal_district: form.federal_district,
    url: form.url,
    explanation: form.explanation,
  }))
}

function loadDraft() {
  const raw = localStorage.getItem(DRAFT_KEY)
  if (!raw) return
  try {
    const draft = JSON.parse(raw)
    if (draft.content) form.content = draft.content
    if (draft.published_at) form.published_at = draft.published_at
    if (draft.views !== undefined) form.views = draft.views
    if (draft.federal_district) form.federal_district = draft.federal_district
    if (draft.url) form.url = draft.url
    if (draft.explanation) form.explanation = draft.explanation
  } catch {
    localStorage.removeItem(DRAFT_KEY)
  }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY)
}

// Auto-save on any field change
watch(
  () => ({ ...form }),
  () => { saveDraft() },
  { deep: true },
)

onMounted(() => {
  loadDraft()
})

// ═════════════════════════════════════════════════════════════════════════════
// Date helpers
// ═════════════════════════════════════════════════════════════════════════════

function disabledFutureDate(time: Date): boolean {
  return time.getTime() > Date.now()
}

// ═════════════════════════════════════════════════════════════════════════════
// AI handler
// ═════════════════════════════════════════════════════════════════════════════

async function handleAnalyze() {
  if (!form.content) {
    ElMessage.warning('Введите текст поста перед анализом')
    return
  }
  if (form.content.length > 8000) {
    ElMessage.warning('Текст слишком длинный для AI-анализа. Максимум 8000 символов.')
    return
  }
  isAnalyzing.value = true
  try {
    const result = await summarizeText(form.content)

    // Populate editable explanation
    form.explanation = result.summary

    // Populate district (if determined by AI)
    if (result.federal_district && result.federal_district !== 'НЕОПР') {
      form.federal_district = result.federal_district
    } else {
      form.federal_district = 'НЕОПР'
    }

    // Store full analysis data for meta display
    analysisData.summary = result.summary
    analysisData.federal_district = result.federal_district
    analysisData.region_name = result.region_name
    analysisData.confidence = result.confidence
    analysisData.category = result.category
    analysisData.is_complaint = result.is_complaint

    const districtMsg = result.federal_district !== 'НЕОПР'
      ? `Округ: ${result.federal_district}${result.region_name ? ` (${result.region_name})` : ''}`
      : 'Округ не определён'
    ElMessage.success(`Анализ завершён. ${districtMsg}`)
  } catch (error: any) {
    if (error.response?.status === 503) {
      ElMessage.warning('AI временно недоступен. Заполните поля вручную.')
    } else if (error.response?.status === 504) {
      ElMessage.warning('Превышено время ожидания AI. Попробуйте снова.')
    } else {
      ElMessage.error('Ошибка при анализе текста')
    }
  } finally {
    isAnalyzing.value = false
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// Submit handler
// ═════════════════════════════════════════════════════════════════════════════

async function handleSubmit() {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return

  isSubmitting.value = true
  try {
    // Build payload — include analysis data if explanation is present
    const payload: {
      content: string
      published_at?: string
      views?: number
      url?: string
      federal_district?: string
      explanation?: string
      category?: string
      confidence?: number
      is_complaint?: boolean
      region_name?: string
    } = {
      content: form.content,
      published_at: form.published_at || undefined,
      views: form.views,
      url: form.url || undefined,
      federal_district: form.federal_district || undefined,
    }

    // Include pre-analyzed data if available
    if (form.explanation) {
      payload.explanation = form.explanation
      payload.category = analysisData.category || undefined
      payload.confidence = analysisData.confidence || undefined
      payload.is_complaint = analysisData.is_complaint || undefined
      payload.region_name = analysisData.region_name || undefined
    }

    const response = await createManualPost(payload)

    if (!response.created && response.duplicate_of) {
      duplicatePostId.value = response.duplicate_of
      duplicateDialogVisible.value = true
      return
    }

    ElMessage.success(`Пост #${response.id} создан.`)
    clearDraft()
    handleClear()
    router.push('/posts')
  } catch (error: any) {
    if (error.response?.status === 409) {
      duplicatePostId.value = extractDuplicatePostId(error.response.data)
      duplicateDialogVisible.value = true
      return
    }
    if (error.response?.status === 422) {
      const detail = error.response.data?.detail
      ElMessage.error(`Ошибка валидации: ${Array.isArray(detail) ? detail.map((d: any) => d.msg).join(', ') : detail}`)
      return
    }
    ElMessage.error('Ошибка при создании поста')
  } finally {
    isSubmitting.value = false
  }
}

function handleClear() {
  form.content = ''
  form.published_at = ''
  form.views = undefined
  form.federal_district = ''
  form.url = ''
  form.explanation = ''
  analysisData.summary = ''
  analysisData.federal_district = ''
  analysisData.region_name = null
  analysisData.confidence = 0
  analysisData.category = ''
  analysisData.is_complaint = false
  clearDraft()
  formRef.value?.resetFields()
}

function goToPost(postId: number) {
  duplicateDialogVisible.value = false
  router.push(`/posts?highlight=${postId}`)
}
</script>

<style scoped>
.manual-add-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--el-text-color-primary);
}

.page-subtitle {
  color: var(--el-text-color-secondary);
  margin-bottom: 24px;
  font-size: 14px;
}

.form-card {
  padding: 8px;
}

.ai-icon {
  margin-right: 4px;
}

.analysis-meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
